import React from "react"
import { Link, withRouter } from "react-router-dom"
import { getMemberFullName } from "../../../derivedSelectors"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import { useSelector } from "react-redux"
import styled from "styled-components"
import { MultiPathSVG } from "../SVG"
import L10n from "lib/carrot-l10n"
import { RoundedRectangle } from "../atoms/roundedrectangle/RoundedRectangle"
import { NotLarge } from "../atoms/Responsive"
import { RRIconBar } from "../atoms/roundedrectangle/RRIconBar"
import { Spacer } from "../atoms/Atoms"
import { BodySmall } from "../atoms/Bodies"
import { RetinaImage } from "../atoms/images/RetinaImage"
import { Header } from "components/views/atoms/Header"
import { H1, H2Library, H5 } from "@carrotfertility/carotene"
import { getPreferredName } from "../../../reducers/userInfo"
import { FormattedMessage } from "react-intl"
import { PiiText } from "services/tracking"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const StyledRetinaImage = styled(RetinaImage)`
  margin-top: ${(props) => props.top}px;
`

export const TitleWithIcon = ({ title, icon, top }) => (
  <div className="flex">
    <StyledRetinaImage name={icon} height={36} width={36} top={top} />
    <Spacer width={0.5} />
    <H1 variant="primary">{title}</H1>
  </div>
)

export const CenteredLoadingIndicator = ({ id = "centered-loading-indicator", ...otherProps }) => (
  <div {...otherProps} className="flex items-center justify-center flex-auto">
    <div className="lds-ellipsis" id={id}>
      <div />
      <div />
      <div />
      <div />
    </div>
  </div>
)

const StyledResponsiveWrapper = styled.div`
  display: flex;
  flex: auto;
  flex-direction: row;
  flex-wrap: wrap;
  margin: -${(props) => props.padding}rem;
  & > * {
    min-width: 232px;
    margin: ${(props) => props.padding}rem;
    flex-grow: 1;
    width: calc(50% - ${(props) => props.padding * 2}rem);
    height: auto;
  }
`

export const ResponsiveWrapper = ({ children, padding }) => (
  <StyledResponsiveWrapper padding={padding}>{children}</StyledResponsiveWrapper>
)

const TtcStyledResponsiveWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  grid-auto-rows: 1fr;
  grid-gap: 1em;
  margin: 1em 0;
`

export const TtcResponsiveWrapper = ({ children, padding }) => (
  <TtcStyledResponsiveWrapper padding={padding}>{children}</TtcStyledResponsiveWrapper>
)

export const StylelessButton = styled.button`
  cursor: pointer;
  padding: 0;
  border: 0;
  background-color: transparent;
  &:focus {
    outline: none;
  }
`

export const BackArrowLink = withRouter(({ history, children }) => (
  <StylelessButton onClick={history.goBack}>
    <MultiPathSVG iconName="icn-left-arrow" className="dib mr2" />
    <div className="dib">
      <H5>{children}</H5>
    </div>
  </StylelessButton>
))

export const FadeTransition = ({ children }) => (
  <TransitionGroup>
    <CSSTransition timeout={{ exit: 200, enter: 600, appear: 600 }} classNames="fade" appear>
      {children}
    </CSSTransition>
  </TransitionGroup>
)

export const HomeHeader = () => {
  const memberFullName = useSelector(getMemberFullName)
  const preferredName = useSelector(getPreferredName)
  const displayName = preferredName ? preferredName : memberFullName

  return (
    <Header
      id="home-header"
      titleBar={
        <H2Library variant="primary">
          <FormattedMessage
            defaultMessage="Hello, {displayName}"
            values={{
              displayName: <PiiText>{displayName}</PiiText>
            }}
          />
        </H2Library>
      }
    />
  )
}

export const PageHeader = ({ title, icon = null, button = null }) => {
  const titleBarWithIcon = (
    <div className="flex flex-row flex-wrap justify-between items-center">
      <TitleWithIcon icon={icon} title={title} />
      {button && <div className="db-l dn-m dn fl">{button}</div>}
    </div>
  )
  return (
    <>
      <Header titleBar={titleBarWithIcon} />
      {button && (
        <NotLarge>
          <Spacer height={1} />
          {button}
        </NotLarge>
      )}
    </>
  )
}

export const BackToPageHeader = ({ pathName, path }) => {
  const titleBarWithBackArrow = (
    <Link className="no-underline" to={path}>
      <div className="dim">
        <H5>
          <span className="dib carrot-orange" style={{ marginRight: 7 }}>
            <MultiPathSVG iconName="icn-left-arrow" />
          </span>
          <span>{pathName}</span>
        </H5>
        <Spacer height={0.375} />
      </div>
    </Link>
  )
  return <Header titleBar={titleBarWithBackArrow} />
}

export const LathamAndWatkinsInformational = ({ showEECopy }) => (
  <RoundedRectangle backgroundColor="near-white">
    <RRIconBar iconName="icn-help" backgroundColor="white-60" />
    <BodySmall color="black-60">
      {showEECopy
        ? l`Please note, this list includes all Carrot-eligible expenses. Due to varying local laws, regulations, religious decrees, and professional guidelines, some services might not be available to you in your country or region. Please reach out to your care navigator directly to talk more in depth about the options available to you.`
        : l`Please note, due to varying local laws, regulations, religious decrees, and professional guidelines, some services might not be available to you in your country or region. Please reach out to your care navigator directly to talk more in depth about the options available to you.`}
    </BodySmall>
  </RoundedRectangle>
)
